import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, ExternalLink, Trash2, Pencil, Wifi, Ticket, ClipboardList, Users, Award, Star, Wand2 } from "lucide-react";
import { api } from "../lib/api";

export const TYPES = [
  ["wifi", "Wi-Fi доступ", "Гость сканирует QR, проходит шаги и получает пароль от Wi-Fi", Wifi],
  ["promo", "Промокод", "Выдача промокода со скидкой после прохождения шагов", Ticket],
  ["survey", "Опрос", "Анкета для гостей с наградой за прохождение", ClipboardList],
  ["lead", "Сбор контактов", "Сбор заявок и контактов потенциальных клиентов", Users],
  ["loyalty", "Лояльность", "Накопительные визиты: отсканировал N раз — получил награду", Award],
  ["review", "Отзыв → подарок", "Подарок за отзыв на Яндекс, Google и других платформах", Star],
  ["custom", "Свой сценарий", "Произвольный набор этапов под вашу задачу", Wand2],
];
export const TYPE_LABELS = Object.fromEntries(TYPES.map(([v, l]) => [v, l]));

const translit = (s) => {
  const map = { а: "a", б: "b", в: "v", г: "g", д: "d", е: "e", ё: "e", ж: "zh", з: "z", и: "i", й: "y", к: "k", л: "l", м: "m", н: "n", о: "o", п: "p", р: "r", с: "s", т: "t", у: "u", ф: "f", х: "h", ц: "c", ч: "ch", ш: "sh", щ: "sch", ъ: "", ы: "y", ь: "", э: "e", ю: "yu", я: "ya" };
  return s.toLowerCase().split("").map((ch) => map[ch] ?? ch).join("").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);
};

export default function Campaigns() {
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", type: "wifi" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/campaigns").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try {
      const base = translit(form.name) || form.type;
      const taken = new Set(items.map((c) => c.slug));
      let slug = base;
      for (let n = 2; taken.has(slug); n++) slug = `${base}-${n}`;
      const { data } = await api.post("/admin/campaigns", { name: { ru: form.name, uz: form.name, en: form.name }, type: form.type, slug });
      toast.success("Кампания создана");
      setShowNew(false);
      window.location.href = `/admin/campaigns/${data.id}`;
    } catch (e) { toast.error(e.response?.data?.detail === "slug_taken" ? "Адрес (slug) уже занят" : "Ошибка"); }
    setBusy(false);
  };
  const toggle = async (c) => { await api.put(`/admin/campaigns/${c.id}`, { active: !c.active }); load(); };
  const del = async (c) => { if (window.confirm("Удалить кампанию?")) { await api.delete(`/admin/campaigns/${c.id}`); load(); } };

  return (
    <div className="space-y-4" data-testid="admin-campaigns">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-black text-2xl">Кампании / QR-коды</h1>
        <button data-testid="campaign-create-btn" className="admin-btn" onClick={() => setShowNew(!showNew)}><Plus size={16} /> Создать</button>
      </div>
      {showNew && (
        <div className="bg-white rounded-2xl p-4 border border-gray-100 space-y-3" data-testid="campaign-create-form">
          <div className="text-sm font-extrabold">1. Выберите тип кампании</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {TYPES.map(([v, l, desc, Icon]) => (
              <button key={v} data-testid={`new-campaign-type-${v}`} onClick={() => setForm({ ...form, type: v })}
                className={`text-left rounded-xl border-2 p-3 transition ${form.type === v ? "border-[#FEBC10] bg-yellow-50" : "border-gray-100 hover:border-gray-200"}`}>
                <div className="flex items-center gap-2 font-extrabold text-sm"><Icon size={16} className={form.type === v ? "text-[#FF0000]" : "text-gray-400"} /> {l}</div>
                <div className="text-xs text-gray-500 font-semibold mt-1">{desc}</div>
              </button>
            ))}
          </div>
          <div className="text-sm font-extrabold pt-1">2. Название</div>
          <div className="flex flex-wrap gap-2 items-center">
            <input data-testid="new-campaign-name" autoFocus className="admin-input !w-72" placeholder='Например: «Wi-Fi в зале»' value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} onKeyDown={(e) => e.key === "Enter" && form.name && create()} />
            <button data-testid="new-campaign-submit" className="admin-btn" disabled={!form.name || busy} onClick={create}>Создать кампанию</button>
          </div>
          <p className="text-xs text-gray-400 font-semibold">Адрес ссылки создастся автоматически, все настройки (этапы, баннеры, Wi-Fi) — на следующем экране.</p>
        </div>
      )}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead><tr className="text-left text-xs text-gray-400 uppercase">
            <th className="px-4 py-3">Название</th><th>Тип</th><th>Ссылка</th><th>Сканы</th><th>Завершено</th><th>CR%</th><th>Посл. скан</th><th>Статус</th><th></th>
          </tr></thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-t border-gray-50 hover:bg-gray-50/50" data-testid={`campaign-row-${c.slug}`}>
                <td className="px-4 py-3 font-bold"><Link to={`/admin/campaigns/${c.id}`} className="hover:text-[#FF0000]">{c.name?.ru}</Link></td>
                <td><span className="px-2 py-0.5 rounded-full bg-yellow-50 border border-yellow-200 text-xs font-bold whitespace-nowrap">{TYPE_LABELS[c.type] || c.type}</span></td>
                <td className="text-gray-500">/c/{c.slug}</td>
                <td>{c.scans || 0}</td>
                <td>{c.completions || 0}</td>
                <td className="font-bold">{c.conversion}%</td>
                <td className="text-xs text-gray-400">{c.last_scan ? c.last_scan.slice(0, 10) : "—"}</td>
                <td>
                  <button data-testid={`campaign-toggle-${c.slug}`} onClick={() => toggle(c)}
                    className={`px-2.5 py-1 rounded-full text-xs font-bold ${c.active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                    {c.active ? "ВКЛ" : "ВЫКЛ"}
                  </button>
                </td>
                <td className="px-3">
                  <div className="flex gap-1.5">
                    <Link data-testid={`campaign-edit-${c.slug}`} to={`/admin/campaigns/${c.id}`} className="p-1.5 rounded-lg bg-gray-100 hover:bg-yellow-100"><Pencil size={14} /></Link>
                    <a data-testid={`campaign-flyer-${c.slug}`} href={`/flyer/${c.slug}`} target="_blank" rel="noreferrer" title="Флаер" className="p-1.5 rounded-lg bg-gray-100 hover:bg-red-100 text-[#FF0000] font-black text-xs leading-none flex items-center">QR</a>
                    <a href={`${window.location.origin}/c/${c.slug}`} target="_blank" rel="noreferrer" className="p-1.5 rounded-lg bg-gray-100 hover:bg-yellow-100"><ExternalLink size={14} /></a>
                    <button data-testid={`campaign-delete-${c.slug}`} onClick={() => del(c)} className="p-1.5 rounded-lg bg-gray-100 hover:bg-red-100 text-red-500"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
